import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button } from "../../../../../../components/Button";
import { MediumCircleContainer } from "../../../../../../components/CircleContainer/styles";
import ConfirmModal from "../../../../../../components/ConfirmModal";
import FixedFooterBar from "../../../../../../components/FixedFooterBar";
import * as Fonts from "../../../../../../components/Fonts";
import { PlusPromo } from "../../../../../../components/PlusPromo";
import { notifySaveError } from "../../../../../../components/SaveErrorNotice";
import { AppState, useAppState } from "../../../../../../contexts/App";
import { useSite } from "../../../../../../contexts/Site";
import { PAGES_PATH, useCurrentPage, withPage } from "../../../../../../hooks/CurrentPage";
import { useQueryUrl } from "../../../../../../hooks/QueryUrl";
import PlusIcon from "../../../../../../icons/Plus";
import {
  duplicateSection,
  getPages,
  getPageSections,
  moveSection,
  setPageSections,
} from "../../../../../../model/pages";
import { availableSections } from "../../../../../../model/sectionDefinitions";
import { Site } from "../../../../../../model/site";
import { api } from "../../../../../../services/api";
import { siteHasActivePlusSections } from "../../sections";
import { AddSectionModal } from "../AddSectionModal";
import { MoveSectionModal } from "../MoveSectionModal";
import CardItem from "./components/Card";
import { AddSectionCard, CardItemWrapper, Container, ContinueButtonSection, SectionsBox } from "./styles";

/** Whether a page can have more than one of this kind of section (the welcome, the form… can't). */
const isMultiple = (type: string, subType?: string) =>
  !!availableSections.find(section => section.type === type && section.subType === subType)?.multiple;

const SectionsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();
  const queryUrl = useQueryUrl();

  const { pageId } = useCurrentPage();

  // the home's sections and the pages copied too (the list may be one of theirs): what the context holds is never
  // changed in place
  const copySite = {
    ...site,
    sections: [...site.sections],
    pages: site.pages?.map(page => ({ ...page, sections: [...page.sections] })),
  } as Site;
  const sections = getPageSections(copySite, pageId);

  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [sectionIdRemoving, setSectionIdRemoving] = useState<string>();
  const [sectionIdMoving, setSectionIdMoving] = useState<string>();
  const movingSection = sections.find(({ id }) => id === sectionIdMoving);
  const goPremiumUrl = appState.editingSiteId
    ? `/subscription/features?site=${appState.editingSiteId}`
    : "/subscription/features";

  const isContinueButtonAvailable = sections.filter(({ active }) => active).length > 0;
  // Whether we got here from the "não quero os recursos PLUS" review loop, regardless
  // of the draft's current state - drives goToPreview's routing decision below.
  const isPlusReviewLoop = queryUrl.get("plusRequired") === "1";
  // Whether the notice should actually be visible right now: reacts to the live
  // draft, not just the URL flag above, so as soon as the last active PLUS section is
  // removed/deactivated, the notice disappears on the same click - no extra
  // navigation needed to "confirm" the cleanup.
  const showPlusRequiredNotice = isPlusReviewLoop && siteHasActivePlusSections(copySite);
  const editorFlow = appState.published ? "edit" : "create";
  const goPremiumFlowUrl = `${goPremiumUrl}${goPremiumUrl.includes("?") ? "&" : "?"}flow=${editorFlow}`;

  const onActiveClick = (sectionId: string, active: boolean) => {
    sections.find(({ id }) => id === sectionId)!.active = active;
    updateSiteState();
  };

  const onRemoveClick = (sectionId: string) => {
    setSectionIdRemoving(sectionId);
  };

  const removeSection = () => {
    const index = sections.findIndex(({ id }) => id === sectionIdRemoving);
    sections.splice(index, 1);

    updateSiteState();
    setSectionIdRemoving(undefined);
  };

  const updateSiteState = () => {
    setSite(copySite);

    const copyAppState = {
      ...appState,
      editingSiteModified: true,
    } as AppState;

    // This site already exists (SectionsList is only reachable after Title's first
    // save), so this can't hit the free Keepos limit - just avoid an unhandled
    // rejection now that saveDraft returns a promise.
    api.saveDraft(copyAppState.editingSiteId as string, copySite).catch(notifySaveError);
    setAppState(copyAppState);
  };

  const moveTo = (from: number, to: number) => {
    const next = [...sections];
    const [section] = next.splice(from, 1);
    next.splice(to, 0, section);
    setPageSections(copySite, pageId, next);
    updateSiteState();
  };

  // a copy right after it
  const duplicate = (index: number) => {
    sections.splice(index + 1, 0, duplicateSection(sections[index]));
    updateSiteState();
  };

  // to the end of the page picked: it leaves this one
  const sendTo = (to: string | null) => {
    if (sectionIdMoving) {
      moveSection(copySite, pageId, sectionIdMoving, to);
      updateSiteState();
    }
    setSectionIdMoving(undefined);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const itemsClone = Array.from(sections);
    const [reorderedItem] = itemsClone.splice(result.source.index, 1);
    itemsClone.splice(result.destination.index, 0, reorderedItem);

    setPageSections(copySite, pageId, itemsClone);

    updateSiteState();
  };

  const goToPreview = () => {
    if (!isContinueButtonAvailable) {
      return;
    }

    if (isPlusReviewLoop) {
      if (siteHasActivePlusSections(copySite)) {
        navigate(goPremiumFlowUrl);
        return;
      }

      // A brand-new (never-published) Keepo still needs to pick a URL, so send it to
      // the publish page. An already-published site doesn't - drop it back into the
      // preview with the PLUS offer skipped instead of a "Seu endereço" screen with
      // a disabled, already-set URL field.
      if (!appState.published) {
        navigate("/editor/publish?skipPlusOffer=1");
        return;
      }

      navigate("/editor/style?flow=publish&skipPlusOffer=1");
      return;
    }

    navigate("/editor/style?flow=publish");
  };

  const getSectionPath = (type: string, id: string, subType?: string) => {
    const path = subType ? `/editor/${type}/${subType}` : `/editor/${type}`;
    return withPage(`${path}?id=${id}`, pageId);
  };

  return (
    <Container>
      <SectionsBox>
        {/* Why it can't go on as Free, on top of the list it's about */}
        {showPlusRequiredNotice && (
          <PlusPromo
            className="plus-notice"
            title={t("pages.editor.sections.plusNotice.title")}
            description={t("pages.editor.sections.plusNotice.description")}
            cta={t("pages.editor.sections.plusNotice.cta")}
            onClick={() => navigate(goPremiumFlowUrl)}
          />
        )}
        {/* What to do here, right above the list */}
        <Fonts.Label className="help">{t("pages.editor.sections.help.message")}</Fonts.Label>
        {/* @ts-ignore */}
        <DragDropContext onDragEnd={handleDragEnd}>
          {/* @ts-ignore */}
          <Droppable droppableId="sections" direction="vertical">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {sections.map(({ id, active, type, subType }, index) => (
                  // @ts-ignore
                  <Draggable key={id} index={index} draggableId={id}>
                    {provided => (
                      <CardItemWrapper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}>
                        <CardItem
                          key={id}
                          sectionType={type}
                          sectionSubtype={subType}
                          active={active}
                          showClassicPlusBadge={showPlusRequiredNotice}
                          onClick={() => navigate(getSectionPath(type, id, subType))}
                          onActiveClick={active => onActiveClick(id, active)}
                          actions={[
                            {
                              label: t("pages.editor.sections.actions.edit"),
                              onSelect: () => navigate(getSectionPath(type, id, subType)),
                            },
                            // the welcome stays on the home; without another page there's nowhere to send it
                            ...(type === "welcome"
                              ? []
                              : [
                                  {
                                    label: t("pages.editor.sections.actions.send"),
                                    disabled: !getPages(copySite).length,
                                    onSelect: () => setSectionIdMoving(id),
                                  },
                                ]),
                            {
                              label: t("pages.editor.sections.actions.up"),
                              disabled: index === 0,
                              onSelect: () => moveTo(index, index - 1),
                            },
                            {
                              label: t("pages.editor.sections.actions.down"),
                              disabled: index === sections.length - 1,
                              onSelect: () => moveTo(index, index + 1),
                            },
                            {
                              label: t("pages.editor.sections.actions.duplicate"),
                              // a one-of-a-kind section (the welcome, the form…) can't be twice on a page
                              disabled: !isMultiple(type, subType),
                              onSelect: () => duplicate(index),
                            },
                            {
                              label: t("pages.editor.sections.actions.delete"),
                              danger: true,
                              onSelect: () => onRemoveClick(id),
                            },
                          ]}
                        />
                      </CardItemWrapper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder as React.ReactNode}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {/* The list ends with a way to add one more (same as the bar's "+ Seção") */}
        <CardItemWrapper>
          <AddSectionCard type="button" onClick={() => setShowAddSectionModal(true)}>
            <span className="icon">
              <PlusIcon width={14} height={14} color="currentColor" />
            </span>
            {t("pages.editor.sections.actions.add.button.title")}
          </AddSectionCard>
        </CardItemWrapper>
      </SectionsBox>
      {sectionIdRemoving && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.editor.sections.actions.remove.confirmModal.title")}
          cancelTitle={t("pages.editor.sections.actions.remove.confirmModal.cancelButton")}
          confirmButtonTitle={t("pages.editor.sections.actions.remove.confirmModal.confirmButton")}
          onCancel={() => setSectionIdRemoving(undefined)}
          onConfirm={removeSection}
        />
      )}
      {showAddSectionModal && <AddSectionModal onClose={() => setShowAddSectionModal(false)} />}
      {movingSection && (
        <MoveSectionModal
          site={copySite}
          section={movingSection}
          from={pageId}
          onMove={sendTo}
          onClose={() => setSectionIdMoving(undefined)}
        />
      )}
      <FixedFooterBar>
        <div className="add-new-section" onClick={() => setShowAddSectionModal(true)}>
          <MediumCircleContainer
            title={t("pages.editor.sections.actions.add.button.title")}
            $color="var(--color-brand-blue)">
            <PlusIcon width={16} height={16} color="var(--color-neutral-white)" />
          </MediumCircleContainer>
          <h6>{t("pages.editor.sections.actions.section.text")}</h6>
        </div>
        <ContinueButtonSection>
          {!isPlusReviewLoop ? (
            // the home's and each page's sections go back to the pages, which go on to the style
            <Button type="INFO" size="LG" onClick={() => navigate(PAGES_PATH)}>
              {t("pages.editor.sections.actions.backToPages")}
            </Button>
          ) : (
            <Button type="INFO" size="LG" disabled={!isContinueButtonAvailable} onClick={() => goToPreview()}>
              {t("pages.editor.sections.actions.continue.button.title")}
            </Button>
          )}
        </ContinueButtonSection>
      </FixedFooterBar>
    </Container>
  );
};

export default SectionsList;
